import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { StraightArrowUpIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { linker, getGetCardsQueryKey } from '@vlabs/api/point-api/client';
import { queryClient } from '@vlabs/api/queryClient';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { MoveListForm } from './forms';
import type { ListId } from '@vlabs/api/luna-api/model';
import type { CardId } from '@vlabs/api/point-api/model';

const displayName = 'MoveCardsToListButton';
const testid = buildTestid(displayName);

export interface MoveCardsToListButtonProps extends
  WithDataTestid,
  ButtonProps {
  fromListId: ListId;
  cardIds: CardId[];
}

export function MoveCardsToListButton({
  fromListId,
  cardIds,
  'data-testid': dataTestId = undefined,
  ...props
}: MoveCardsToListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal({ size: 'small' });

  const moveCardsToListMutation = useMutation({
    mutationFn: async (attachListId: string) => {
      try {
        await linker({
          action: 'attach',
          card_ids: cardIds,
          list_id: attachListId,
        });
      } catch (e) {
        toast.error(t('features:move_cards_to_list.toast.error.attach'));
        throw e;
      }

      try {
        await linker({
          action: 'detach',
          card_ids: cardIds,
          list_id: fromListId,
        });
      } catch (e) {
        toast.error(t('features:move_cards_to_list.toast.error.detach'));
        throw e;
      }

      toast.success(t('features:move_cards_to_list.toast.success'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getGetCardsQueryKey(),
      });
      modal.close();
    },
  });

  return (
    <React.Fragment>
      {modal.wrap(<MoveListForm
        onCancel={modal.close}
        onSubmit={moveCardsToListMutation.mutateAsync}
      />)}
      <Control.Button
        icon={<StraightArrowUpIcon />}
        kind="primary"
        onClick={modal.open}
        size="small"
        variant="flat-inverted"
        {...testid(dataTestId)}
        {...props}
      >
        {t('features:move_cards_to_list.buttons.move')}
      </Control.Button>
    </React.Fragment>
  );
}
